import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Trash2, Plus } from 'lucide-react';
import { jobTitlesApi } from '../../../../api/job-titles';
import { JobTitle } from '../../../../types';
import { Button } from '../../../../components/ui/atoms/button';
import { Input } from '../../../../components/ui/atoms/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../../components/ui/molecules/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../../components/ui/organisms/dialog';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '../../../../components/ui/organisms/form';
import { getErrorMessage } from '../utils';

const jobTitleSchema = z.object({
  name: z.string().min(1, 'Name is required'),
});
type JobTitleValues = z.infer<typeof jobTitleSchema>;


export function JobTitlesTab() {
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: jobTitles, isLoading } = useQuery({
    queryKey: ['job-titles'],
    queryFn: () => jobTitlesApi.list().then((r) => r.data),
  });

  const jobTitlesList: JobTitle[] = jobTitles ?? [];

  const createForm = useForm<JobTitleValues>({
    resolver: zodResolver(jobTitleSchema),
    defaultValues: { name: '' },
  });

  const createMutation = useMutation({
    mutationFn: (v: JobTitleValues) => jobTitlesApi.create(v),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-titles'] });
      createForm.reset();
      setCreateOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => jobTitlesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-titles'] });
      setDeleteId(null);
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Job Title
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Create Job Title</DialogTitle>
            </DialogHeader>
            <Form {...createForm}>
              <form onSubmit={createForm.handleSubmit((v) => createMutation.mutate(v))} className="space-y-4">
                <FormField
                  control={createForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl><Input placeholder="e.g. Software Engineer" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {createMutation.error && (
                  <p className="text-sm text-destructive">
                    {getErrorMessage(createMutation.error, 'Failed to create job title')}
                  </p>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={createMutation.isPending}>
                    {createMutation.isPending ? 'Creating…' : 'Create'}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteId} onOpenChange={(o) => { if (!o) setDeleteId(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Job Title</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This job title will be permanently removed.
          </p>
          {deleteMutation.error && (
            <p className="text-sm text-destructive">
              {getErrorMessage(deleteMutation.error, 'Failed to delete job title')}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Users</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobTitlesList.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}className="text-center text-muted-foreground">No job titles found.</TableCell>
              </TableRow>
            )}
            {jobTitlesList.map((jt) => (
              <TableRow key={jt.id}>
                <TableCell className="font-medium">{jt.name}</TableCell>
                <TableCell className="text-muted-foreground">{jt.userCount ?? 0}</TableCell>
                <TableCell className="flex items-center gap-1">
                  {(jt.userCount ?? 0) === 0 && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setDeleteId(jt.id)}
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
